import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, FileSpreadsheet, FileText, Image as ImageIcon, Folder, ChevronRight, HardDrive } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounce } from '@/hooks/useDebounce';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { callDrive, type DriveStatus } from '@/lib/googleDrive';

export const FEATURE_META = {
  id: 'bulk-import-drive-picker',
  label: 'Choose a Spreadsheet from Google Drive',
  description: 'Browse Recent, My Drive, Shared with me and Shared drives (or search) to pick an inventory spreadsheet — Google Sheet, Excel or CSV — to bulk import without downloading it first.',
  triggerType: 'dialog' as const,
  personas: ['seller', 'buyer_seller'] as ('seller' | 'buyer_seller')[],
};

type DriveFile = { id: string; name: string; mimeType: string; modifiedTime: string | null };
type View = 'recent' | 'my' | 'shared' | 'drives';
type Crumb = { id: string; name: string };

const FOLDER = 'application/vnd.google-apps.folder';
const VIEWS: { value: View; label: string; short?: string }[] = [
  { value: 'recent', label: 'Recent' },
  { value: 'my', label: 'My Drive' },
  { value: 'shared', label: 'Shared with me', short: 'Shared' },
  { value: 'drives', label: 'Shared drives' },
];

function base64ToFile(b64: string, name: string, type: string) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type });
}

export type DrivePickKind = 'spreadsheet' | 'document' | 'media';

const COPY: Record<DrivePickKind, { description: string; noun: string }> = {
  spreadsheet: { description: 'Browse your folders or search. Google Sheets, Excel and CSV files are shown.', noun: 'spreadsheets' },
  document: { description: 'Browse your folders or search. PDFs, Google Docs, Word, Excel, CSV and text files are shown (up to 10 MB).', noun: 'documents' },
  media: { description: 'Browse your folders or search. Photos and videos (MP4, WebM, MOV) up to 10 MB are shown.', noun: 'photos or videos' },
};

/** Shared read-only Google Drive file picker (Bulk Import spreadsheets, listing COAs/lab reports). */
export default function DriveSpreadsheetPicker({ open, onOpenChange, onPicked, kind = 'spreadsheet' }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPicked: (file: File) => void;
  kind?: DrivePickKind;
}) {
  useFeatureRegistration(FEATURE_META);
  const [status, setStatus] = useState<DriveStatus | null>(null);
  const [files, setFiles] = useState<DriveFile[] | null>(null);
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const [picking, setPicking] = useState<string | null>(null);
  const [view, setView] = useState<View>('recent');
  const [path, setPath] = useState<Crumb[]>([]);

  useEffect(() => {
    if (!open) return;
    callDrive<DriveStatus>({ action: 'status' }).then(setStatus).catch((e) => toast.error('Couldn’t reach Google Drive', { description: (e as Error).message }));
  }, [open]);

  const ready = !!status?.enabled && status.connected && !status.reconnectRequired;
  const folderId = path.length ? path[path.length - 1].id : undefined;

  const load = useCallback(async () => {
    setFiles(null);
    try {
      const r = await callDrive<{ files?: DriveFile[] }>({
        action: 'files', kind, view, folderId, query: debounced || undefined,
      });
      setFiles(r.files ?? []);
    } catch (e) {
      toast.error('Couldn’t list your Drive files', { description: (e as Error).message });
      setFiles([]);
    }
  }, [debounced, view, folderId, kind]);
  useEffect(() => { if (open && ready) load(); }, [open, ready, load]);

  const changeView = (v: string) => { setView(v as View); setPath([]); setQuery(''); };

  const pick = async (f: DriveFile) => {
    if (f.mimeType === FOLDER) { setQuery(''); setPath((p) => [...p, { id: f.id, name: f.name }]); return; }
    setPicking(f.id);
    try {
      const r = await callDrive<{ name: string; mimeType: string; base64: string }>({ action: 'download', fileId: f.id, kind });
      onPicked(base64ToFile(r.base64, r.name, r.mimeType));
      onOpenChange(false);
    } catch (e) {
      toast.error('Couldn’t open that file', { description: (e as Error).message });
    } finally {
      setPicking(null);
    }
  };

  const rootLabel = VIEWS.find((v) => v.value === view)!.label;
  const emptyText = debounced
    ? `No ${COPY[kind].noun} found for “${debounced}”.`
    : view === 'drives' && !path.length ? 'You don’t belong to any shared drives.' : `No folders or ${COPY[kind].noun} here.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Choose from Google Drive</DialogTitle>
          <DialogDescription>{COPY[kind].description}</DialogDescription>
        </DialogHeader>
        {!status ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-label="Loading" /></div>
        ) : !status.enabled ? (
          <p className="text-sm text-muted-foreground py-4">Google Drive is turned off for your company. Ask a company admin to turn it on in Company Settings.</p>
        ) : !ready ? (
          <div className="py-4 space-y-3">
            <p className="text-sm text-muted-foreground">{status.reconnectRequired ? 'Your Google Drive connection needs a quick refresh.' : 'Connect your Google Drive first — it takes a few seconds.'}</p>
            <Button asChild variant="outline"><Link to="/profile-settings#connections" onClick={() => onOpenChange(false)}>Go to Connections</Link></Button>
          </div>
        ) : (
          <div className="space-y-3 min-w-0">
            <Input placeholder="Search all of Drive by file name" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search Drive files" />
            {!debounced && (
              <>
                <Tabs value={view} onValueChange={changeView}>
                  <TabsList>
                    {VIEWS.map((v) => <TabsTrigger key={v.value} value={v.value} className="px-2 text-xs sm:px-3 sm:text-sm">{v.short ? <><span className="sm:hidden">{v.short}</span><span className="hidden sm:inline">{v.label}</span></> : v.label}</TabsTrigger>)}
                  </TabsList>
                </Tabs>
                <nav aria-label="Current location" className="flex items-center flex-wrap gap-1 text-sm text-muted-foreground min-w-0">
                  {path.length ? (
                    <button type="button" className="hover:text-foreground" onClick={() => setPath([])}>{rootLabel}</button>
                  ) : (
                    <span className="text-foreground font-medium">{rootLabel}</span>
                  )}
                  {path.map((c, i) => (
                    <span key={c.id} className="flex items-center gap-1 min-w-0">
                      <ChevronRight className="w-3 h-3 shrink-0" />
                      <button type="button" className={`truncate max-w-[12rem] ${i === path.length - 1 ? 'text-foreground font-medium' : 'hover:text-foreground'}`} onClick={() => setPath(path.slice(0, i + 1))}>{c.name}</button>
                    </span>
                  ))}
                </nav>

              </>
            )}
            <div className="h-[min(20rem,45vh)] min-h-40 overlay-scroll divide-y divide-border rounded-md border border-border">
              {files === null ? (
                <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Loading" /></div>
              ) : files.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">{emptyText}</p>
              ) : files.map((f) => {
                const isFolder = f.mimeType === FOLDER;
                const Icon = isFolder ? (view === 'drives' && !path.length ? HardDrive : Folder) : kind === 'document' ? FileText : kind === 'media' ? ImageIcon : FileSpreadsheet;
                return (
                  <button
                    key={f.id}
                    type="button"
                    disabled={!!picking}
                    onClick={() => pick(f)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 disabled:opacity-60"
                  >
                    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm truncate">{f.name}</span>
                      {f.modifiedTime && <span className="block text-xs text-muted-foreground">Edited {new Date(f.modifiedTime).toLocaleDateString()}</span>}
                    </span>
                    {picking === f.id ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Opening" />
                      : isFolder && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
